/// <amd-module name="Wit/WorkItemForm/WorkItemFormView" />
import * as React from "react";
import { getTFSProject, IProjectData } from "Tfs/Platform/Page";
import { IObservableValue, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { equals, format, isGuid, localeIgnoreCaseComparer, startsWith } from "VSS/Core/Util/String";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService } from "VSS/Platform/Settings";
import { publishEvent } from "VSS/Platform/Telemetry";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { Button } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Icon, IIconProps } from "VSSUI/Icon";
import { Intersection } from "VSSUI/Intersection";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabContent } from "VSSUI/Tabs";
import { css } from "VSSUI/Util";
import { ZeroData } from "VSSUI/ZeroData";
import { FormPage } from "Wit/Clients/Wit/Contracts/WorkItemFormLayout";
import { WorkItemTemplate } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { ITeam } from "Wit/Common/BacklogContentService/BacklogContentDataProviderTypes";
import { AddTagsPseudoRefName, processTagsChanges, RemoveTagsPseudoRefName } from "Wit/Common/BulkUpdateUtils/TagUtils";
import { DataProviders } from "Wit/Common/Constants/DataProviders";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { buildDate, MacroToday, tryConvertStringToDate } from "Wit/Common/DateUtils";
import { CoreField, CoreFieldRefNames, DalFields, FieldType } from "Wit/Common/Generated/Constants";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { getCurrentCultureFromLegacyContext } from "Wit/Common/Utilities/FormatUtils";
import { isMeMacro, WiqlOperators } from "Wit/Common/WiqlOperators";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemsHubNavData, IWorkItemsHubNavDataService } from "Wit/Common/WorkItemsHubNavDataService/IWorkItemsHubNavDataService";
import { getDistinctDisplayName } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { BoardsMessages } from "Wit/Messages/Components/BoardsMessages";
import { IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { IUserSettings, IWorkItemNotificationListener } from "Wit/WorkItemForm/Contracts/Contracts";
import { getCurrentCommentFormatSettingAsync } from "Wit/WorkItemForm/Details/WorkItemFormCommentFormatSwitch";
import { getRegisteredInstance } from "Wit/WorkItemForm/Details/WorkItemFormExtensionHost";
import * as Resources from "Wit/WorkItemForm/Resources";
import { ActiveWorkItemService } from "Wit/WorkItemFormExtension/WorkItemFormExtensionService";
import { convertHTMLToMarkdown } from "Wit/WorkItemFormMarkdown/MarkdownUtils";
import { WITCustomerIntelligenceArea, WITPerformanceScenario, WorkItemFormPerfConstants, WorkItemWatermarkAllAction } from "Wit/WorkItemModel/OM";
import { ILink, IWorkItem, IWorkItemBulkSaveResult, WorkItemDiscussionFormat } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemFormUnsavedChangesService } from "Wit/WorkItemModel/Services/WorkItemFormUnsavedChangesService";
import { LegacyWorkItem } from "Wit/WorkItemModel/WorkItem/LegacyWorkItem";
import { IWorkItemSignalRService, IWorkItemUpdatePayload, WorkItemChangeType } from "Wit/WorkItemSignalRTypes/WorkItemSignalRService.Types";
import { AttachmentsTab } from "./Attachments";
import { FileDropZone } from "./Attachments/FileDropZone";
import { collapseButtonClassName, collapsibleSectionClassName, FullScreenSectionContainer } from "./Common/Components";
import { MultiWorkItemFieldSubscriber, WorkItemFieldSubscriber } from "./Common/Components/WorkItemFieldSubscriber";
import { ControlDefaultOptionsContext, WorkItemFormPageContextProvider } from "./Common/Hooks";
import { getWorkItemFormTitle, isNewWorkItemFormRoute } from "./Common/RoutingUtils";
import { IWorkItemFormAttachmentsService, IWorkItemFormCommentService, IWorkItemFormService } from "./Common/Services";
import { IWorkItemFormPropsWithType, IWorkItemFormViewProps } from "./Contracts";
import { WorkItemFormTab } from "./Details";
import { isGroupInvalid } from "./Details/WorkItemFormGroup";
import { WorkItemFormHeader } from "./Header";
import { WorkItemFormNavBar } from "./Header/WorkItemFormNavBar";
import { WorkItemFormSubHeader } from "./Header/WorkItemFormSubHeader";
import { HistoryTab } from "./History";
import { LinksTab } from "./Links";
import { ICultureInfo } from "Wit/Common/Culture";
import { IWorkItemFieldEventData, IWorkItemFieldEventService, IWorkItemFieldEventType } from "Wit/WorkItemModel/Services/IWorkItemFieldEventService";

export class WorkItemFormView extends VssComponent<IWorkItemFormViewProps> {
    private legacyWorkItem: LegacyWorkItem | undefined;
    private selectedTabId: ObservableValue<string>;
    private tabs: ObservableCollection<IVssContributedTab>;
    private workItemModelService: IWorkItemModelService;
    private workItemFormService: IWorkItemFormService;
    private workItemFieldEventService: IWorkItemFieldEventService;
    private unsavedChangesService: IWorkItemFormUnsavedChangesService;
    private attachmentsService: IWorkItemFormAttachmentsService;
    private workItemsNavigationData: IWorkItemsHubNavData | undefined;
    private workItem: IObservableValue<IWorkItem | undefined> = new ObservableValue(undefined);
    private projectId: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private linksCount = new ObservableValue<number>(0);
    private attachmentsCount = new ObservableValue<number>(0);
    private workItemType: IObservableValue<IWorkItemTypeData>;
    private focusDiscussion: IObservableValue<boolean> = new ObservableValue(false);
    private userSettings: IUserSettings | undefined;
    private notificationHostInstances: IWorkItemNotificationListener[] = [];
    private workItemLoadError = new ObservableValue<string | undefined>(undefined);
    private activeWorkItemService: ActiveWorkItemService;
    private currentIterationPath: string | undefined;
    private reassignableShortcutGroupSequenceNumber: number = 0;

    constructor(props: IWorkItemFormViewProps, context: IVssComponentContext) {
        super(props, context);
        this.workItemModelService = context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this.workItemFormService = context.pageContext.getService<IWorkItemFormService>("IWorkItemFormService");
        this.workItemFieldEventService = context.pageContext.getService<IWorkItemFieldEventService>("IWorkItemFieldEventService");
        this.attachmentsService = context.pageContext.getService<IWorkItemFormAttachmentsService>("IWorkItemFormAttachmentsService");
        this.activeWorkItemService = context.pageContext.getService<ActiveWorkItemService>("ms.vss-work-web.active-work-item-service");
    }

    public async componentDidMount() {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.userSettings = contributionService.getData<IUserSettings>("ms.vss-work-web.work-item-layout-user-settings-data-provider");

        const workItemNavDataService = this.context.pageContext.getService<IWorkItemsHubNavDataService>("IWorkItemsHubNavDataService");

        this.workItemsNavigationData = workItemNavDataService.getNavigationData();

        const id = navigationService.getRouteValues()["id"];
        let workItemId = Number(id) ? parseInt(id) : undefined;

        let workItemType = navigationService.getRouteValues()["workItemTypeName"];
        this.unsavedChangesService = this.context.pageContext.getService<IWorkItemFormUnsavedChangesService>("IWorkItemFormUnsavedChangesService");

        const currentTeam = contributionService.getData<ITeam | undefined>(DataProviders.TeamContextDataProvider);
        const teamSettingsService = this.context.pageContext!.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings(undefined, currentTeam?.id);
        this.currentIterationPath = teamSettings?.currentIteration?.friendlyPath;

        // don't mark TTI if form is being rendered by another component
        let markTTI = true;
        let shouldRefresh = undefined;
        if ("workItemId" in this.props) {
            workItemId = this.props.workItemId;
            shouldRefresh = this.props.shouldRefresh;
            markTTI = false;
        }
        if ("workItemTypeName" in this.props) {
            workItemType = this.props.workItemTypeName;
            markTTI = false;

            if (!("workItemId" in this.props)) {
                workItemId = undefined;
            }
        }
        if ("workItem" in this.props) {
            markTTI = false;
        }

        if (!this.selectedTabId) {
            this.selectedTabId = new ObservableValue("");
        }

        if ("workItem" in this.props) {
            this.workItemType = new ObservableValue(this.props.workItem.getTypeData());
            this.workItem.value = this.props.workItem;

            this.selectedTabId.subscribe(this.onTabSelectionUpdated);
            markTTI && perfService.markTimeToInteractive(WorkItemFormPerfConstants.WorkItemFormArea, WorkItemFormPerfConstants.EditWorkItemView);
        } else if (workItemId) {
            await this.getWorkItemFromId(workItemId);
            if (shouldRefresh) {
                await this.workItem.value?.refresh();
            }
            markTTI && perfService.markTimeToInteractive(WorkItemFormPerfConstants.WorkItemFormArea, WorkItemFormPerfConstants.EditWorkItemView);
        } else if (workItemType) {
            const typeProps = this.props as IWorkItemFormPropsWithType;
            const links = typeProps?.initialValues?.links;
            const initialValuesIncludingTemplate: { [fieldName: string]: any } = await this.loadTemplateIfSpecified(typeProps?.initialValues ?? {});
            await this.getWorkItemFromType(workItemType, initialValuesIncludingTemplate, links);
            markTTI && perfService.markTimeToInteractive(WorkItemFormPerfConstants.WorkItemFormArea, WorkItemFormPerfConstants.CreateWorkItemView);
        }

        this.activeWorkItemService.setActiveWorkItem(this.workItem.value);

        const notificationContributionIds = contributionService.getContributionIdsByType("ms.vss-work-web.work-item-notifications");
        for (const c of notificationContributionIds) {
            // deliberately use .then() to avoid blocking
            getRegisteredInstance(this.context.pageContext, this.workItem.value!, c, {}).then(listener => {
                if (listener) {
                    this.notificationHostInstances.push(listener);
                }
            });
        }

        if (this.shouldUpdateTitle()) {
            this.updateEditorTitle();
            this.workItem.value!.dirty.subscribe(this.updateEditorTitle);
        }

        this.updateLinksAndAttachments();
        this.registerShortcuts();

        if (!this.props.ignoreUnsavedChanges && this.workItem.value) {
            this.unsavedChangesService.register(this.workItem.value);
        }

        const workItemFieldChangedEventOptimizationEnabled = isFeatureFlagEnabled(
            this.context.pageContext,
            FeatureFlags.WorkItemFieldChangedEventOptimizationEnabled,
            false
        );
        if (workItemFieldChangedEventOptimizationEnabled) {
            this.workItemFieldEventService.subscribe(this.onFieldsUpdate, IWorkItemFieldEventType.WorkItemFieldsUpdated);
        } else {
            this.workItem.value?.getFieldWatermark().subscribe(this.onFieldUpdate);
        }

        this._connectSignalR();

        this.props.onLoad?.(this.workItem.value);
        this.props.onLegacyLoad?.(this.getLegacyWorkItem());
        this.props.onRegisterTryShowSaveDialog?.(this.tryShowSaveDialog);
    }

    private onTabSelectionUpdated = () => {
        if (this.props.onTabChange) {
            const tabId = this.tabs.value.find(tab => tab.id === this.selectedTabId.value)?.id ?? "";
            this.props.onTabChange(tabId);
        }
    };

    private async loadTemplateIfSpecified(initialValues: { [fieldName: string]: any }): Promise<{ [fieldName: string]: any }> {
        let newValues: { [fieldName: string]: any };
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues: { [key: string]: string } = historyService.getState();
        const templateId: string = routeValues["templateId"];
        const ownerId: string = routeValues["ownerId"];
        if (templateId && ownerId) {
            const client: IWorkItemTrackingRestClient = getWorkItemTrackingClient(this.context.pageContext);
            const project: IProjectData = getTFSProject(this.context.pageContext)!;
            const template: WorkItemTemplate = await client.getTemplate(project.id, ownerId, templateId);
            const historyField = template.fields[CoreFieldRefNames.History];

            if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false) && historyField) {
                const settingService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                const settings = await getCurrentCommentFormatSettingAsync(settingService);
                if (settings.CommentFormatState === WorkItemDiscussionFormat.Markdown) {
                    template.fields[CoreFieldRefNames.History] = await convertHTMLToMarkdown(historyField);
                }
            }

            const assignedToValue = template.fields[CoreFieldRefNames.AssignedTo];
            if (assignedToValue && isMeMacro(assignedToValue, false)) {
                const currentUser = this.context.pageContext.getService<IVssPageService>("IVssPageService").getData()?.user;
                if (currentUser) {
                    template.fields[CoreFieldRefNames.AssignedTo] = getDistinctDisplayName(currentUser.displayName, currentUser.uniqueName);
                }
            }

            newValues = { ...initialValues, ...template.fields };
        } else {
            newValues = { ...initialValues };
        }

        Object.keys(routeValues)
            .filter(v => v.startsWith("[") && v.endsWith("]"))
            .forEach(v => {
                const fieldRefName: string = v.substr(1, v.length - 2);
                newValues[fieldRefName] = routeValues[v];
            });

        return newValues;
    }

    private onFieldsUpdate = ({ fieldDefinitionIds }: IWorkItemFieldEventData) => {
        if (!this.workItem.value || !fieldDefinitionIds || !fieldDefinitionIds.length) {
            return;
        }

        this.notificationHostInstances.forEach(hostInstance => {
            if (hostInstance.onFieldChanged) {
                const fieldChangeMap: { [key: string]: any } = {};

                fieldDefinitionIds.forEach(fieldId => {
                    const referenceName = this.workItem.value!.fieldMapById[fieldId].fieldDefinition.referenceName;
                    fieldChangeMap[referenceName] = this.workItem.value!.getFieldValueById(fieldId);
                });

                hostInstance.onFieldChanged({ id: this.workItem.value!.id, changedFields: fieldChangeMap });
            }
        });
    };
    
    private onFieldUpdate = (value: number, action: string) => {
        this.notificationHostInstances.forEach(hostInstance => {
            if (hostInstance.onFieldChanged && this.workItem.value) {
                // If the action is all-fields, pass every field in the map.
                // Otherwise, only pass the one that matches the action.
                const fieldChangeMap: { [key: string]: any } = {};
                if (action == WorkItemWatermarkAllAction) {
                    this.workItem.value.fields.forEach(field => {
                        const referenceName = field.fieldDefinition.referenceName;
                        fieldChangeMap[referenceName] = this.workItem.value!.getFieldValueByName(referenceName);
                    });
                } else {
                    const changedFieldId = parseInt(action);
                    const referenceName = this.workItem.value.fieldMapById[changedFieldId].fieldDefinition.referenceName;
                    fieldChangeMap[referenceName] = this.workItem.value.getFieldValueById(changedFieldId);
                }

                hostInstance.onFieldChanged({ id: this.workItem.value.id, changedFields: fieldChangeMap });
            }
        });
    };

    public async componentDidUpdate(prevProps: IWorkItemFormViewProps) {
        const prevWorkItem = this.workItem.value;
        let workItemChanged = false;

        if (this.shouldUpdateTitle()) {
            this.workItem.value!.dirty.unsubscribe(this.updateEditorTitle);
        }

        if (
            "workItemId" in this.props &&
            (("workItemId" in prevProps && this.props.workItemId !== prevProps.workItemId) || !("workItemId" in prevProps))
        ) {
            workItemChanged = true;
            this.workItem.value = undefined;
            await this.getWorkItemFromId(this.props.workItemId);
        } else if (
            "workItem" in this.props &&
            (("workItem" in prevProps && this.props.workItem !== prevProps.workItem) || !("workItem" in prevProps))
        ) {
            workItemChanged = true;
            this.workItemType = new ObservableValue(this.props.workItem.getTypeData());
            this.workItem.value = this.props.workItem;
        } else if (
            "workItemTypeName" in this.props &&
            (("workItemTypeName" in prevProps && this.props.workItemTypeName !== prevProps.workItemTypeName) || !("workItemTypeName" in prevProps))
        ) {
            workItemChanged = true;
            this.workItem.value = undefined;
            await this.getWorkItemFromType(this.props.workItemTypeName, this.props.initialValues);
        }

        if (workItemChanged) {
            if (!this.props.ignoreUnsavedChanges) {
                prevWorkItem && this.unsavedChangesService.unregister(prevWorkItem);
                this.workItem.value && this.unsavedChangesService.register(this.workItem.value);
            }

            prevWorkItem && this._disconnectSignalR(prevWorkItem.id);
            this._connectSignalR();
            this.updateLinksAndAttachments();
        }

        if (this.shouldUpdateTitle()) {
            this.updateEditorTitle();
            this.workItem.value!.dirty.subscribe(this.updateEditorTitle);
        }

        this.props.onLoad?.(this.workItem.value);
        this.props.onLegacyLoad?.(this.getLegacyWorkItem());
    }

    public componentWillUnmount(): void {
        const workItemFieldChangedEventOptimization = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.WorkItemFieldChangedEventOptimizationEnabled, false);
        if (workItemFieldChangedEventOptimization) {
            this.workItemFieldEventService.unsubscribe(this.onFieldsUpdate, IWorkItemFieldEventType.WorkItemFieldsUpdated);
        } else {
            this.workItem.value?.getFieldWatermark().unsubscribe(this.onFieldUpdate);
        }
        this.workItem.value && !this.props.ignoreUnsavedChanges && this.unsavedChangesService.unregister(this.workItem.value);
        this.workItem.value && this._disconnectSignalR(this.workItem.value.id);
        this.unregisterShortcuts();
        this.selectedTabId?.unsubscribe(this.onTabSelectionUpdated);
        this.notificationHostInstances.forEach((hostInstance: IWorkItemNotificationListener) => {
            if (hostInstance.onUnloaded && this.workItem.value) {
                hostInstance.onUnloaded({ id: this.workItem.value.id });
            }
        });
    }

    public tabIcon(page: FormPage): IIconProps | undefined {
        if (page.pageType === 1) {
            return undefined;
        } else if (page.pageType === 2) {
            return { iconName: "History" };
        } else if (page.pageType === 3) {
            return { iconName: "Link" };
        } else {
            return { iconName: "Attach" };
        }
    }

    public renderTab(page: FormPage, includeDiscussion: boolean): JSX.Element {
        if (page.pageType === 1) {
            const gridLayoutEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.NewBoardsWorkItemGridLayout, false);
            return <WorkItemFormTab gridLayoutEnabled={gridLayoutEnabled} page={page} includeDiscussion={includeDiscussion} onExtensionLoaded={this.onExtensionLoaded} />;
        } else if (page.pageType === 2) {
            return <HistoryTab pageContext={this.context.pageContext} />;
        } else if (page.pageType === 3) {
            return <LinksTab />;
        } else {
            return <AttachmentsTab />;
        }
    }

    /**
     * Work item notification, group, page, and control extensions can all register host instances
     * that listen for notification events.
     */
    private onExtensionLoaded = (workItem: IWorkItem, hostInstance: IWorkItemNotificationListener | null) => {
        if (hostInstance) {
            this.notificationHostInstances.push(hostInstance);
        }
    };

    private setPagesVisibility(pages: FormPage[]): void {
        for (let page of pages) {
            let isPageVisible = false;

            if (page.visible) {
                for (let section of page.sections) {
                    for (let group of section.groups) {
                        let isGroupVisible = false;
                        for (let control of group.controls) {
                            if (control.visible) {
                                isGroupVisible = true;
                                isPageVisible = true;
                                break;
                            }
                        }

                        // Group extensions are always visible
                        if (group.isContribution && group.visible) {
                            isPageVisible = true;
                            isGroupVisible = true;
                        }

                        group.visible = isGroupVisible;
                    }
                }

                if (page.isContribution) {
                    isPageVisible = true;
                }

                page.visible = isPageVisible;
            }
        }
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.normal}>
                <Observer
                    focusDiscussion={this.focusDiscussion}
                    workItem={{
                        observableValue: this.workItem,
                        filter: () => {
                            this.activeWorkItemService.setActiveWorkItem(this.workItem.value);
                            return true;
                        }
                    }}
                    workItemLoadError={this.workItemLoadError}
                >
                    {(rootProps: { focusDiscussion: boolean; workItem: IWorkItem | undefined; workItemLoadError: string | undefined }) => {
                        if (rootProps.workItem) {
                            return (
                                <WorkItemFormPageContextProvider
                                    value={{
                                        focusDiscussion: rootProps.focusDiscussion,
                                        pageContext: this.context.pageContext,
                                        updateFocusDiscussion: shouldFocus => {
                                            this.focusDiscussion.value = shouldFocus;
                                        },
                                        workItem: rootProps.workItem
                                    }}
                                >
                                    <WorkItemFieldSubscriber
                                        key="wif-wi-type-subscriber"
                                        fieldId={DalFields.WorkItemTypeId}
                                        onUpdate={() => (this.workItemType.value = rootProps.workItem!.getTypeData())}
                                    />
                                    <MultiWorkItemFieldSubscriber
                                        fieldIds={[DalFields.AttachedFiles, DalFields.RelatedLinks, DalFields.LinkedFiles, DalFields.BISURI]}
                                        onUpdate={() => this.updateLinksAndAttachments()}
                                    />
                                    <FileDropZone
                                        onFilesDropped={files => rootProps.workItem && this.attachmentsService.attachFiles(rootProps.workItem, files)}
                                    >
                                        <Observer workItemType={this.workItemType}>
                                            {(innerProps: { workItemType: IWorkItemTypeData }) => {
                                                this.tabs = new ObservableCollection<IVssContributedTab>();
                                                const form = this.workItemFormService.getWorkItemFormLayout(innerProps.workItemType);
                                                this.setPagesVisibility(form.pages);
                                                const visibleTabs: FormPage[] = form.pages.filter(p => p.visible);
                                                const firstTabId: string = visibleTabs.length > 0 ? visibleTabs[0].id : "";

                                                this.tabs.push(
                                                    visibleTabs.map(tab => {
                                                        const icon = this.tabIcon(tab);

                                                        return {
                                                            id: tab.id,
                                                            ariaLabel: tab.label,
                                                            badgeCount: this.getBadgeCount(tab),
                                                            name: icon ? undefined : tab.label,
                                                            order: tab.pageType,
                                                            iconProps: icon,
                                                            render: () => (
                                                                <ErrorBoundary key={tab.id} handlerComponentType="tfs.page-error-handler">
                                                                    {this.renderTab(tab, tab.id === firstTabId)}
                                                                </ErrorBoundary>
                                                            ),
                                                            renderBadge:
                                                                tab.pageType === 1
                                                                    ? () => (
                                                                        <Observer infoText={rootProps.workItem!.infoText}>
                                                                            {() => {
                                                                                return tab.sections.some(section =>
                                                                                    section.groups.some(group =>
                                                                                        isGroupInvalid(group, rootProps.workItem!)
                                                                                    )
                                                                                ) ? (
                                                                                    <Icon
                                                                                        ariaLabel={format(Resources.InvalidTabOrGroup, tab.label)}
                                                                                        className="padding-left-4 error-text"
                                                                                        key="invalid-control-icon"
                                                                                        iconName="AlertSolid"
                                                                                    />
                                                                                ) : null;
                                                                            }}
                                                                        </Observer>
                                                                    )
                                                                    : undefined
                                                        } as IVssContributedTab;
                                                    })
                                                );

                                                if (this.props.selectedTabId !== undefined) {
                                                    const tabId = this.props.selectedTabId;
                                                    let tabToSelect: IVssContributedTab | undefined;
                                                    if (isGuid(tabId)) {
                                                        // tab added by an extension
                                                        tabToSelect = this.tabs.value.find(tab => tab.id === tabId);
                                                    } else {
                                                        tabToSelect = this.tabs.value.find(tab => tab.id === tabId);
                                                        if (!tabToSelect && tabId.length > 0) {
                                                            // tabId is in the format <process name>.<work item type>.<tab name> e.g. Agile.Bug.System_Links
                                                            const tabName = tabId.substring(tabId.lastIndexOf(".") + 1);
                                                            tabToSelect = this.tabs.value.find(tab => tab.id.includes(tabName));
                                                        }
                                                    }
                                                    if (!tabToSelect) {
                                                        // select the first tab by default if no matching tab is found
                                                        tabToSelect = this.tabs.value[0];
                                                    }
                                                    if (this.selectedTabId && tabToSelect.id !== this.selectedTabId.value) {
                                                        this.selectedTabId.value = tabToSelect.id;
                                                    }
                                                }

                                                if (!this.selectedTabId || !this.tabs.value.some(tab => tab.id === this.selectedTabId.value)) {
                                                    this.selectedTabId = new ObservableValue(this.tabs.value[0].id);
                                                }

                                                const showNavBar =
                                                    this.workItemsNavigationData &&
                                                    isNewWorkItemFormRoute(this.context.pageContext) &&
                                                    !this.props.onDismiss;

                                                return (
                                                    <Page
                                                        className={css(
                                                            this.props.className,
                                                            "work-item-form-page flex-grow flex-column v-scroll-hidden form-height"
                                                        )}
                                                        selectedTabId={this.selectedTabId}
                                                        tabProviders={this.tabs}
                                                    >
                                                        <ControlDefaultOptionsContext.Provider
                                                            value={{
                                                                showChromeBorder: this.userSettings?.fieldChromeBorder === true,
                                                                showEmptyReadOnlyFields: form.showEmptyReadOnlyFields === true
                                                            }}
                                                        >
                                                            <BoardsMessages className="boards-message-bar flex-noshrink" />
                                                            <ConditionalChildren renderChildren={rootProps.workItem!.saving}>
                                                                <div className="absolute-fill work-item-saving-layer" />
                                                            </ConditionalChildren>
                                                            {showNavBar && (
                                                                <WorkItemFormNavBar
                                                                    onNavigateNext={() =>
                                                                        this.workItemsNavigationData?.selectedItemIndexInVisibleList !== undefined &&
                                                                        this.navigateBetweenWorkItems(
                                                                            this.workItemsNavigationData!.selectedItemIndexInVisibleList! + 1
                                                                        )
                                                                    }
                                                                    onNavigatePrevious={() =>
                                                                        this.workItemsNavigationData?.selectedItemIndexInVisibleList !== undefined &&
                                                                        this.navigateBetweenWorkItems(
                                                                            this.workItemsNavigationData!.selectedItemIndexInVisibleList! - 1
                                                                        )
                                                                    }
                                                                    navigationData={this.workItemsNavigationData!}
                                                                />
                                                            )}
                                                            <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                                                                <WorkItemFormHeader
                                                                    onDelete={
                                                                        this.props.onDelete || this.props.onLegacyDelete ? this.onDelete : undefined
                                                                    }
                                                                    onCommentCountClick={() => this.onFocusDiscussion()}
                                                                    onDismiss={
                                                                        this.props.onDismiss || this.props.onLegacyDismiss
                                                                            ? this.onDismiss
                                                                            : undefined
                                                                    }
                                                                    onRefreshed={this.onRefreshed}
                                                                    onReset={this.onReset}
                                                                    onSave={(callback, errorCallback) => this.onSave(callback, errorCallback)}
                                                                    onToggleFullscreen={this.props.onToggleFullscreen}
                                                                    systemControls={form.systemControls}
                                                                    shortcutGroupName={this.getShortcutGroupName()}
                                                                    currentIterationPath={this.currentIterationPath}
                                                                />
                                                            </ErrorBoundary>
                                                            <FullScreenSectionContainer>
                                                                <div className="full-height flex-column">
                                                                    <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                                                                        <WorkItemFormSubHeader
                                                                            selectedTabId={this.selectedTabId}
                                                                            systemControls={form.systemControls}
                                                                            shortcutGroupName={this.getShortcutGroupName()}
                                                                        />
                                                                    </ErrorBoundary>
                                                                    <Intersection>
                                                                        <div className="work-item-form-page-content page-content page-content-top flex-row flex-grow v-scroll-auto full-height full-width flex-wrap">
                                                                            <TabContent key={"tab:" + this.selectedTabId.value} />
                                                                        </div>
                                                                    </Intersection>
                                                                </div>
                                                            </FullScreenSectionContainer>
                                                        </ControlDefaultOptionsContext.Provider>
                                                    </Page>
                                                );
                                            }}
                                        </Observer>
                                    </FileDropZone>
                                </WorkItemFormPageContextProvider>
                            );
                        } else if (rootProps.workItemLoadError) {
                            return (
                                <ContentWithCloseButton onDismiss={this.props.onDismiss && this.onDismiss}>
                                    <ZeroData
                                        primaryText={Resources.ServerError}
                                        secondaryText={rootProps.workItemLoadError}
                                        imagePath={getAssetLocation(
                                            this.context.pageContext,
                                            "ms.vss-work-web/new-work-items-hub-content/Illustrations/general-something-wrong-on-server.svg"
                                        )}
                                        imageAltText={Resources.ServerError}
                                    />
                                </ContentWithCloseButton>
                            );
                        } else {
                            return (
                                <ContentWithCloseButton onDismiss={this.props.onDismiss && this.onDismiss}>
                                    <Spinner className="flex-grow" />
                                </ContentWithCloseButton>
                            );
                        }
                    }}
                </Observer>
            </Surface>
        );
    }

    private getShortcutGroupName = (): string => {
        return `${Resources.WorkItemFormKeyboardShortcutGroup}${this.workItem.value?.id}`;
    };

    private onRefreshed = () => {
        this.notificationHostInstances.forEach(hostInstance => {
            if (hostInstance.onRefreshed && this.workItem.value) {
                hostInstance.onRefreshed({ id: this.workItem.value.id });
            }
        });
    };

    private onReset = () => {
        this.notificationHostInstances.forEach(hostInstance => {
            if (hostInstance.onReset && this.workItem.value) {
                hostInstance.onReset({ id: this.workItem.value.id });
            }
        });
    };

    private updateEditorTitle = () => {
        if (this.workItem.value) {
            document.title = getWorkItemFormTitle(this.workItem.value);
        }
    };

    private shouldUpdateTitle() {
        return this.workItem.value && (this.props.updateDocumentTitle || (isNewWorkItemFormRoute(this.context.pageContext) && !this.props.onDismiss));
    }

    private getLegacyWorkItem(): LegacyWorkItem | undefined {
        if (this.legacyWorkItem) {
            return this.legacyWorkItem;
        }

        if (this.workItem.value) {
            return new LegacyWorkItem(this.context.pageContext, this.workItem.value, []);
        }

        return undefined;
    }

    private async getWorkItemFromId(id: number): Promise<void> {
        try {
            const workItem = await this.workItemModelService.getWorkItem(id, { includeHistory: false });
            this.workItemType = new ObservableValue(workItem.getTypeData());
            this.workItem.value = workItem;
        } catch (ex) {
            if (ex instanceof Error) {
                this.workItemLoadError.value = ex.message;
            } else if (ex?.toString()) {
                this.workItemLoadError.value = ex.toString();
            } else {
                this.workItemLoadError.value = Resources.ErrorLoadingWorkItem;
            }
        }
    }

    private async getWorkItemFromType(type: string, initialValues?: { [fieldName: string]: any }, links?: ILink[]): Promise<void> {
        const project = getTFSProject(this.context.pageContext);

        if (!project || !project.id) {
            throw Error(Resources.ProjectInfoFailure);
        } else {
            this.projectId.value = project.id;
        }
        const currentCulture = getCurrentCultureFromLegacyContext();
        const types = await this.workItemModelService.getWorkItemTypeData(this.projectId.value, [type]);
        if (types && types.length === 1) {
            const workItem = await this.workItemModelService.createWorkItem(types[0], this.projectId.value);
            for (const [fieldName, fieldValue] of Object.entries(initialValues ?? {})) {
                const fieldDefinition = workItem.fields.find(field => field.fieldDefinition.referenceName === fieldName)?.fieldDefinition;

                let fieldValueToUse = fieldValue;

                if (fieldDefinition?.type === FieldType.DateTime && typeof fieldValue === "string") {
                    fieldValueToUse = this.handleDateTimeField(fieldValue, currentCulture);
                } else if (fieldDefinition?.type === FieldType.TreePath && fieldName === CoreFieldRefNames.IterationPath && typeof fieldValue === "string") {
                    fieldValueToUse = this.handleIterationField(fieldValue);
                }

                workItem.setFieldValueByName(fieldName, fieldValueToUse, { handleExceptions: true });
            }
            if (initialValues?.[AddTagsPseudoRefName]) {
                processTagsChanges(workItem, { fieldName: AddTagsPseudoRefName, value: initialValues[AddTagsPseudoRefName] });
            }
            if (initialValues?.[RemoveTagsPseudoRefName]) {
                processTagsChanges(workItem, { fieldName: RemoveTagsPseudoRefName, value: initialValues[RemoveTagsPseudoRefName] });
            }

            if (links) {
                workItem.addLinks(links);
            }
            this.workItemType = new ObservableValue(workItem.getTypeData());
            this.workItem.value = workItem;
        } else {
            throw Error(format(Resources.InvalidWorkItemType, type));
        }
    }

    private handleDateTimeField(fieldValue: string, currentCulture: ICultureInfo): string | Date | undefined {
        let valueToUse: string | Date = fieldValue;
        const useBuildDate = startsWith(fieldValue, MacroToday, /* ignore case */ true);
        const parsedDateValue = useBuildDate ?
            buildDate(fieldValue)
            : tryConvertStringToDate(fieldValue, { currentCulture, format: "d" });

        if (parsedDateValue && !isNaN(parsedDateValue.getTime())) {
            valueToUse = parsedDateValue;
        }

        return valueToUse;
    }

    private handleIterationField(fieldValue: string): string  {
        if (this.currentIterationPath === undefined) {
            return fieldValue;
        }
       
        const needToReplaceValue = equals(fieldValue, WiqlOperators.MacroCurrentIteration, /* ignore case */ true);
       
        const fieldValueToUse = needToReplaceValue
            ? this.currentIterationPath
            : fieldValue;

        return fieldValueToUse;
    }

    private onFocusDiscussion() {
        this.focusDiscussion.value = true;
        this.selectedTabId.value = this.tabs.value[0].id;
    }

    private async onSave(callback: (result: IWorkItemBulkSaveResult[]) => void, errorCallback?: ((error: any) => void) | undefined) {
        if (this.workItem.value) {
            try {
                const result = await this.workItem.value.save(undefined, /*keepDeletedLinks*/ true);
                const workItem = this.workItem.value;

                if (workItem) {
                    // Added in case the ID updates on save (like a new work item being saved)
                    // Otherwise if we get the WorkItem through the WorkItemModelService then it will be a new object
                    this.workItemModelService.addWorkItemToMap(workItem.id, workItem);
                    if (this.props.onSaveComplete) {
                        this.props.onSaveComplete(workItem, /*includeHistory*/ false);
                    }
                }

                const legacyWorkItem = this.getLegacyWorkItem();
                if (legacyWorkItem && this.props.onLegacySave) {
                    this.props.onLegacySave(legacyWorkItem);
                }

                callback(result);

                this.notificationHostInstances.forEach(hostInstance => {
                    if (hostInstance.onSaved) {
                        hostInstance.onSaved({ id: workItem.id });
                    }
                });
            } catch (error) {
                errorCallback && errorCallback(error);
            }
        }
    }

    private onDelete = async () => {
        if (this.props.onLegacyDelete) {
            const legacyWorkItem = this.getLegacyWorkItem();
            if (legacyWorkItem) {
                this.props.onLegacyDelete(legacyWorkItem);
            }
        }

        this.props.onDelete?.();
    };

    private onDismiss = async (): Promise<void> => {
        const dialogResult = await this.tryShowSaveDialog();

        if (!dialogResult.canDismiss) {
            return;
        }

        if (this.props.onLegacyDismiss) {
            const legacyWorkItem = this.getLegacyWorkItem();
            if (legacyWorkItem) {
                this.props.onLegacyDismiss(legacyWorkItem);
            }
        }

        this.props.onDismiss?.();
    };

    private tryShowSaveDialog = async (): Promise<{ canDismiss: boolean }> => {
        const saveCallback = () =>
            this.onSave(
                () => {
                    this.workItemFormService.resetHistoryData();
                },
                () => {
                    this.workItemFormService.resetHistoryData();
                }
            );

        const confirmationPromise =
            this.workItem.value?.manualDirty.value && !this.props.ignoreUnsavedChanges
                ? this.unsavedChangesService.showMessage(this.workItem.value, saveCallback)
                : Promise.resolve(true);
        return { canDismiss: await confirmationPromise };
    };

    private getBadgeCount(page: FormPage): number | IObservableValue<number> | undefined {
        switch (page.pageType) {
            case 3:
                return this.linksCount;
            case 4:
                return this.attachmentsCount;
            default:
                return undefined;
        }
    }

    private async navigateBetweenWorkItems(index: number) {
        let workItemId = this.workItemsNavigationData?.workItemIds?.[index];

        if (workItemId) {
            const prevWorkItem = this.workItem.value;

            if (prevWorkItem?.dirty.value) {
                if (!(await this.unsavedChangesService.showMessage())) {
                    return;
                }
            }

            if (this.shouldUpdateTitle()) {
                prevWorkItem!.dirty.unsubscribe(this.updateEditorTitle);
            }

            prevWorkItem && this.unsavedChangesService.unregister(prevWorkItem);

            prevWorkItem?.reset();

            this.workItem.value = undefined;

            this.workItemsNavigationData = {
                ...this.workItemsNavigationData!,
                selectedItemIndexInVisibleList: index,
                lastTriagedWorkItemId: workItemId
            };

            this.context.pageContext
                .getService<IWorkItemsHubNavDataService>("IWorkItemsHubNavDataService")
                .setNavigationData(this.workItemsNavigationData);

            await this.getWorkItemFromId(workItemId);
            prevWorkItem && this._disconnectSignalR(prevWorkItem.id);
            this._connectSignalR();

            this.updateEditorTitle();
            this.workItem.value!.dirty.subscribe(this.updateEditorTitle);

            this.props.onLoad?.(this.workItem.value);
            this.props.onLegacyLoad?.(this.getLegacyWorkItem());

            prevWorkItem?.reset();
        }
    }

    private updateLinksAndAttachments = () => {
        const links = this.workItem.value?.getLinks() ?? [];
        const attachmentsCount = links.filter(x => {
            return localeIgnoreCaseComparer(x.baseLinkType, "Attachment") === 0;
        }).length;

        this.attachmentsCount.value = attachmentsCount;

        this.linksCount.value = links.length - attachmentsCount;

        if (this.workItem.value) {
            this.context.pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService").refreshLinkData(this.workItem.value);
            this.context.pageContext
                .getService<IWorkItemFormAttachmentsService>("IWorkItemFormAttachmentsService")
                .getAttachments(this.workItem.value);
        }
    };

    private async _disconnectSignalR(workItemId: number) {
        const signalRService = this._tryGetSignalRService();
        if (!signalRService) {
            return;
        }
        await signalRService.endWatch(workItemId);
    }

    private async _connectSignalR() {
        const signalRService = this._tryGetSignalRService();
        if (!signalRService || !this.workItem.value) {
            return;
        }

        const useSignalRAppPool = isFeatureFlagEnabled(this.context.pageContext, "WebAccess.SignalR.AppPool", false);
        await signalRService.watch(this.workItem.value.id, this._onWorkItemUpdated, useSignalRAppPool);
    }

    private _shouldLoadSignalRService(): boolean {
        const isMember = userHasClaim(this.context.pageContext, UserClaims.Member);

        return isMember && !!this.workItem.value && !this.workItem.value.isDeleted() && !this.workItem.value.isNew();
    }

    private _tryGetSignalRService(): IWorkItemSignalRService | null {
        if (!this._shouldLoadSignalRService()) {
            return null;
        }

        return this.context.pageContext.getService<IWorkItemSignalRService>("ms.vss-work-web.work-item-signalr-service");
    }

    private _shouldWorkItemBeUpdated = (workItem: IWorkItem | undefined, workItemUpdatePayload: IWorkItemUpdatePayload): boolean => {
        // If work item is dirty except history field, don't update
        return (
            !!workItem &&
            workItem.id === workItemUpdatePayload.id &&
            (workItemUpdatePayload.linkOnlyUpdate ||
                workItemUpdatePayload.isCommentReactionUpdate ||
                workItem.revision.value < workItemUpdatePayload.revision) &&
            !workItem.saving.value &&
            !workItem.isDirty(true, [CoreField.History])
        );
    };

    private _onWorkItemUpdated = async (workItemUpdatePayload: IWorkItemUpdatePayload) => {
        const workItem = this.workItem.value;

        if (this._shouldWorkItemBeUpdated(workItem, workItemUpdatePayload)) {
            if (workItemUpdatePayload.isCommentReactionUpdate) {
                const workItemFormCommentService = this.context.pageContext.getService<IWorkItemFormCommentService>("IWorkItemFormCommentService");
                workItemFormCommentService.syncCommentReactions(workItem!, workItemUpdatePayload.commentId);
            } else {
                const isDeleted = workItemUpdatePayload.changeType === WorkItemChangeType.Delete;

                try {
                    await workItem!.refresh({
                        isDeleted,
                        doNotResetComment: true /* Set unsaved comment to the previous value before refresh happens */,
                        doNotResetIfManuallyDirty: true,
                        includeHistory: undefined,
                        doNotResetFields: workItemUpdatePayload.linkOnlyUpdate
                    });
                } catch (error) {
                    publishEvent(
                        this.context.pageContext,
                        WITCustomerIntelligenceArea.WORK_ITEM_TRACKING,
                        WITPerformanceScenario.WORKITEM_SIGNALR_REFRESHERROR,
                        {
                            project: workItem!.project.name,
                            workItemSessionId: workItem!.sessionId,
                            workItemId: workItem!.id,
                            error
                        }
                    );
                }

                publishEvent(
                    this.context.pageContext,
                    WITCustomerIntelligenceArea.WORK_ITEM_TRACKING,
                    WITPerformanceScenario.WORKITEM_SIGNALR_REFRESHCOMPLETE,
                    {
                        project: workItem!.project.name,
                        workItemSessionId: workItem!.sessionId,
                        workItemId: workItem!.id
                    }
                );
            }
        } else {
            // Notify the user there are dirty changes
            // No-op. We will need to get the work item and diff changes with the local changes to see if anything is conflicting.
            // If no conflicting changes, update work item behind the scenes but don't overwrite local changes
            // If there are conflicting changes, show dialog to notify the user
        }
    };

    private registerShortcuts(): void {
        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");

        const latestHotKeyGroup = shortcutService.findNextReassignableGroupByName(Resources.WorkItemFormKeyboardShortcutGroup);
        if (latestHotKeyGroup) {
            this.reassignableShortcutGroupSequenceNumber = latestHotKeyGroup.sequenceNumber + 1;
        }
        const shortcutGroup = { name: Resources.WorkItemFormKeyboardShortcutGroup, sequenceNumber: this.reassignableShortcutGroupSequenceNumber };

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+shift+,"],
            description: Resources.MoveToLeftTabShortcut,
            globalCombos: ["mod+shift+,"],
            action: () => {
                const selectedIndex = this.tabs.value.findIndex(tab => tab.id === this.selectedTabId.value);
                this.selectedTabId.value = this.tabs.value[Math.max(selectedIndex - 1, 0)].id;
            }
        });

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+shift+."],
            description: Resources.MoveToRightTabShortcut,
            globalCombos: ["mod+shift+."],
            action: () => {
                const selectedIndex = this.tabs.value.findIndex(tab => tab.id === this.selectedTabId.value);
                this.selectedTabId.value = this.tabs.value[Math.min(selectedIndex + 1, this.tabs.length - 1)].id;
            }
        });

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+shift+d"],
            description: Resources.GoToDiscussionShortcut,
            globalCombos: ["mod+shift+d"],
            action: ev => {
                this.onFocusDiscussion();
            }
        });

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+shift+/"],
            description: Resources.GoToNextFormGroupShortcut,
            globalCombos: ["mod+shift+/"],
            action: ev => {
                this.onFocusGroup(true);
            }
        });

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+shift+'"],
            description: Resources.GoToPreviousFormGroupShortcut,
            globalCombos: ["mod+shift+'"],
            action: ev => {
                this.onFocusGroup(false);
            }
        });

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+s"],
            description: Resources.SaveChanges,
            globalCombos: ["mod+s"],
            action: () => {
                if (
                    this.workItem.value &&
                    !this.workItem.value.isReadOnly() &&
                    !this.workItem.value.saving.value &&
                    this.workItem.value.dirty.value &&
                    !this.workItem.value.infoText.value.invalid
                ) {
                    this.onSave(this.resetHistoryData, this.resetHistoryData);
                }
            }
        });

        shortcutService.registerReassignableGroupShortcut(shortcutGroup, {
            combos: ["mod+enter"],
            description: Resources.SaveAndClose,
            globalCombos: ["mod+enter"],
            action: () => {
                if (
                    this.workItem.value &&
                    !this.workItem.value.isReadOnly() &&
                    !this.workItem.value.saving.value &&
                    this.workItem.value.dirty.value &&
                    !this.workItem.value.infoText.value.invalid
                ) {
                    this.onSave(() => {
                        this.resetHistoryData();
                        this.props.onDismiss && this.props.onDismiss();
                    }, this.resetHistoryData);
                }
            }
        });
    }

    private resetHistoryData = () => {
        this.context.pageContext?.getService<IWorkItemFormService>("IWorkItemFormService").resetHistoryData();
    };

    private onFocusGroup = (isNext: boolean) => {
        let newIndex = 0;
        const collapseButtonSelector = "." + collapseButtonClassName;
        const sectionSelector = "." + collapsibleSectionClassName;
        const groupCollapseButtons = Array.prototype.slice.call(document.querySelectorAll(collapseButtonSelector));
        const closestSection = document.activeElement?.closest(sectionSelector);
        if (!closestSection) {
            newIndex = isNext ? 0 : groupCollapseButtons.length - 1;
        } else {
            const currentCollapseButton = closestSection.querySelector(collapseButtonSelector);
            const currentFocusIndex = groupCollapseButtons.indexOf(currentCollapseButton);
            if (isNext) {
                // move focus to the next collapse button.
                newIndex = (currentFocusIndex + 1) % groupCollapseButtons.length;
            } else {
                if (document.activeElement !== currentCollapseButton) {
                    // focus is within a group, set focus to the current group's button.
                    newIndex = currentFocusIndex;
                } else {
                    // focus is on a collapse button, move to the previous collapse button.
                    newIndex = currentFocusIndex > 0 ? currentFocusIndex - 1 : groupCollapseButtons.length - 1;
                }
            }
        }
        groupCollapseButtons[newIndex]?.focus();
    };

    private unregisterShortcuts(): void {
        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.removeReassignableShortcutGroup(Resources.WorkItemFormKeyboardShortcutGroup, this.reassignableShortcutGroupSequenceNumber);
    }
}

Components.add("work-item-form-view-component", WorkItemFormView);

function ContentWithCloseButton(props: React.PropsWithChildren<{ onDismiss?: () => void }>) {
    return (
        <div className="full-height full-width flex-column justify-center">
            <div className="full-width flex-row justify-end">
                {props.onDismiss && (
                    <Button
                        ariaLabel={Resources.Close}
                        iconProps={{ iconName: "ChromeClose" }}
                        onClick={() => props.onDismiss!()}
                        subtle
                        tooltipProps={{ text: Resources.Close }}
                    />
                )}
            </div>
            <div className="flex-grow flex-row justify-center">{props.children}</div>
        </div>
    );
}
